import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductFilterToken } from '@sleed/bizcommerce-api';
import { productFiltersApi } from '~/lib/api/client';

export const productFilterKeys = {
	all: ['product-filters'] as const,
	list: () => [...productFilterKeys.all, 'list'] as const
};

export function useProductFiltersList() {
	return useQuery<ProductFilterToken[]>({
		queryKey: productFilterKeys.list(),
		queryFn: async () => (await productFiltersApi.getAll()).data.items ?? []
	});
}

export function useProductFilterMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: productFilterKeys.all });
	const create = useMutation({
		mutationFn: async (token: ProductFilterToken) => (await productFiltersApi.insert(token)).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: ProductFilterToken) => (await productFiltersApi.update(token)).data,
		onSuccess: invalidate
	});
	// Persists the dynamic-filter definition (Filter JSON) separately from name/description.
	const setFilter = useMutation({
		mutationFn: async (token: ProductFilterToken) => (await productFiltersApi.setFilter(token)).data,
		onSuccess: invalidate
	});
	// Assignments (attributes/statuses given to the matched products); the server applies them on save.
	const setAssignments = useMutation({
		mutationFn: async (token: ProductFilterToken) => (await productFiltersApi.setAssignments(token)).data,
		onSuccess: invalidate
	});
	// Populates a manual-run filter and applies its assignments.
	const runManually = useMutation({
		mutationFn: async (id: number) => (await productFiltersApi.runManually(id)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await productFiltersApi.remove(id)).data,
		onSuccess: invalidate
	});
	return { create, update, setFilter, setAssignments, runManually, remove };
}
